import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Initials } from "../components/PeoplePicker";
import { ProjectForm, shortDay } from "../components/ProjectDialogs";
import { endpoints, type Project } from "../lib/api";

/** Every project you're in (every project, for admins), as cards. A project
 * is a named group of meetings: everyone in it can open all of them. */
export function ProjectsPage() {
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });
  const projects = useQuery({ queryKey: ["projects"], queryFn: endpoints.projects });
  const [creating, setCreating] = useState(false);
  const list = projects.data ?? [];

  return (
    <div className="page-body projects-page">
      <header className="meetings-head">
        <div>
          <h1>Projects</h1>
          <p className="meetings-sub">
            {projects.data ? `${list.length} project${list.length === 1 ? "" : "s"}` : "Loading…"}
            {me.data?.sees_all && " · as an admin you see everyone's"}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setCreating(true)}>
          <IconPlus />
          New project
        </button>
      </header>

      {projects.error && <div className="error">{(projects.error as Error).message}</div>}

      {projects.isLoading ? (
        <p className="dash-empty">Loading…</p>
      ) : list.length === 0 ? (
        <section className="card project-empty">
          <h2>Start your first project</h2>
          <p>
            Group the meetings that belong together — a client, a launch, a team — and add the people working on it.
            Everyone in a project can open every meeting in it.
          </p>
          <button className="btn-primary" onClick={() => setCreating(true)}>Create project</button>
        </section>
      ) : (
        <div className="project-grid">
          {list.map((project) => <ProjectCard key={project.id} project={project} />)}
        </div>
      )}

      {creating && (
        <ProjectForm
          csrfToken={me.data?.csrf_token ?? ""}
          onClose={() => setCreating(false)}
          onSaved={(saved) => navigate(`/projects/${saved.id}`)}
        />
      )}
    </div>
  );
}

function ProjectCard({ project }: { project: Project }) {
  const shown = project.members.slice(0, 5);
  return (
    <Link className="card project-card" to={`/projects/${project.id}`}>
      <span className="project-card-icon" aria-hidden><IconFolder /></span>
      <h2>{project.name}</h2>
      {project.description && <p className="project-card-desc">{project.description}</p>}
      <div className="project-card-foot">
        <span className="project-faces" title={project.members.map((m) => m.name ?? m.email).join(", ")}>
          {shown.map((member) => <Initials key={member.id} name={member.name ?? member.email} />)}
          {project.members.length > shown.length && (
            <span className="share-initials more">+{project.members.length - shown.length}</span>
          )}
        </span>
        <span className="project-card-facts">
          {project.meeting_count} meeting{project.meeting_count === 1 ? "" : "s"}
          {project.last_meeting_at && ` · last ${shortDay(project.last_meeting_at)}`}
        </span>
      </div>
    </Link>
  );
}

function IconPlus() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function IconFolder() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    </svg>
  );
}
